const drawerEvents = {
    OnLineItemUpdate: 'action:cart:updateLineItem',
    onQuantityChange: 'on:QuantityChange',
    triggerCartChange: 'action:cart:updateCart',
};

class DrawerCart extends HTMLElement {
    constructor() {
        super();
        this.currentLineItem;
        this.lineItemError = null;
        this.cartIdentifier = this.getAttribute('render-section');
        this.checkoutContainer = document.querySelector(
            '[cart-checkout-container]'
        );

        this.sectionId =
            this.getAttribute('data-sectionId') || this.cartIdentifier;

        /*
         * Cart requests are intentionally batched and locked here.
         * This prevents multiple cart/change requests from completing out of
         * order and rendering stale totals or stale line-item keys.
         */
        this.pendingLineItemUpdates = new Map();
        this.cartUpdateTimer = null;
        this.cartUpdateInProgress = false;
        this.discountRefreshPending = false;
        this.cartRefreshRequestId = 0;
        this.cartUpdateDelay = 120;

        /* Store bound callbacks so listeners can be removed correctly. */
        this.boundHandleLineItem = this.handleLineItem.bind(this);
        this.boundRenderCart = this.renderCart.bind(this);
        this.boundFetchAndRenderCartItems =
            this.fetchAndRenderCartItems.bind(this);
        this.boundShowCartPopup = this.handleShowCartPopup.bind(this);
        this.boundDiscountStart = () => {
            this.discountRefreshPending = true;
            clearTimeout(this.cartUpdateTimer);
            this.pendingLineItemUpdates.clear();
            this.setCartControlsDisabled(true);
        };
        this.boundDiscountFailed = () => {
            this.discountRefreshPending = false;
            if (!this.cartUpdateInProgress) this.setCartControlsDisabled(false);
        };
        document.addEventListener('aminoz:discount-start', this.boundDiscountStart);
        document.addEventListener('aminoz:discount-failed', this.boundDiscountFailed);

        this.addEventListener(
            _EVENT_HELPER.OnLineItemUpdate,
            this.boundHandleLineItem
        );

        /* Listening for this event to update cart items and show cart drawer base on products Added to cart via product-form,product-card component 
            Usage: document.dispatchEvent(new CustomEvent(_EVENT_HELPER.triggerCartChange, { detail: {}}));
            Note: Make sure to pass the `detail` @object with the data consisting of sections that can fetched via section rendering Api functionality of Shopify
        */

        document.addEventListener(
            _EVENT_HELPER.triggerCartChange,
            this.boundRenderCart
        );

        /* Listening for this event to update cart items and show cart drawer based on custom functionality of the theme 
            Scenarios: When user adds a product to cart, the cart drawer should be updated and shown
            Usage: document.dispatchEvent(new CustomEvent(_EVENT_HELPER.updateAndShowCart, { detail: {}}));
        */
        document.addEventListener(
            _EVENT_HELPER.updateAndShowCart,
            this.boundFetchAndRenderCartItems
        );

        document.addEventListener(
            _EVENT_HELPER.showCartPopup,
            this.boundShowCartPopup
        );
    }

    handleShowCartPopup(e) {
        if (!e?.detail?.sections) return;
        this.renderCartContents(e.detail);
        this._updateCartItemCount(e.detail);
    }

    _getCartItemCount(data) {
        const cartSection = data?.sections?.[this.cartIdentifier];
        if (!cartSection) {
            return Number(this.dataset.cartitemcount || 0);
        }

        const parseCartSectionHTML = new DOMParser().parseFromString(
            cartSection,
            'text/html'
        );
        const cartElement = parseCartSectionHTML.querySelector(this.localName);

        return Number(cartElement?.dataset?.cartitemcount || 0);
    }

    renderCart(e) {
        const data = e?.detail;

        /*
         * Both the cart drawer and cart page listen to the same document event.
         * Ignore events that do not contain the section for this component.
         */
        if (!data?.sections?.[this.cartIdentifier]) return;

        this.renderCartContents(data);
        // Update dependent Elements to be render
        this.renderElementsBasedOnCartItems(data);
        if (
            this.cartIdentifier == 'cart-drawer' &&
            !this.closest('cart-drawer-component')?.isDrawerOpen
        ) {
            HELPER_UTIL.dispatchCustomEvent(_EVENT_HELPER.showDrawer, this, {
                drawerType: 'cart-drawer',
            });
        }
    }

    _get(property) {
        return this[property];
    }

    _set(property, value) {
        this[property] = value;
    }

    _updateCartItemCount(data) {
        this.cartItemCount = this._getCartItemCount(data);
        this.dataset.cartitemcount = this.cartItemCount;
    }

    showDrawerToastMessage({ responseObj, messageType }) {
        HELPER_UTIL.dispatchCustomEvent(
            _EVENT_HELPER.triggerToastMessage,
            document,
            {
                toastType: 'common-errors',
                messageType: messageType,
                message: responseObj.message,
            }
        );
    }

    quantityElementAction({ line, quantity }) {
        const enable = () => {
            this.querySelectorAll('quantity-selector').forEach((el) =>
                el.setAttribute('is-disabled', 'false')
            );
        };

        const disable = () => {
            this.querySelectorAll('quantity-selector').forEach((el) =>
                el.setAttribute('is-disabled', 'true')
            );
        };

        const resetToPreviousValue = () => {
            this.querySelector(`[item-index="${line}"]`)
                ?.querySelectorAll('quantity-selector input')
                .forEach((el) => {
                    el.value = (Number(quantity) - 1).toString();
                });
        };

        return {
            enable,
            disable,
            resetToPreviousValue,
        };
    }

    setCartControlsDisabled(disabled) {
        this.toggleAttribute('aria-busy', disabled);

        this.querySelectorAll('quantity-selector').forEach((el) => {
            el.setAttribute('is-disabled', disabled ? 'true' : 'false');
        });

        this.querySelectorAll('[remove-lineItem]').forEach((el) => {
            if (disabled) {
                if (!Object.prototype.hasOwnProperty.call(el.dataset, 'cartPreviousTabindex')) {
                    el.dataset.cartPreviousTabindex = el.getAttribute('tabindex') ?? '';
                }
                if (!Object.prototype.hasOwnProperty.call(el.dataset, 'cartPreviousPointerEvents')) {
                    el.dataset.cartPreviousPointerEvents = el.style.pointerEvents || '';
                }
                el.setAttribute('aria-disabled', 'true');
                el.setAttribute('tabindex', '-1');
                el.style.pointerEvents = 'none';
            } else {
                const hasStoredState =
                    Object.prototype.hasOwnProperty.call(
                        el.dataset,
                        'cartPreviousTabindex'
                    ) ||
                    Object.prototype.hasOwnProperty.call(
                        el.dataset,
                        'cartPreviousPointerEvents'
                    );

                /* Newly rendered controls were never disabled, so leave them intact. */
                if (!hasStoredState) return;

                const previousTabindex = el.dataset.cartPreviousTabindex;
                const previousPointerEvents =
                    el.dataset.cartPreviousPointerEvents;

                el.removeAttribute('aria-disabled');

                if (previousTabindex === '') {
                    el.removeAttribute('tabindex');
                } else {
                    el.setAttribute('tabindex', previousTabindex);
                }

                el.style.pointerEvents = previousPointerEvents || '';
                delete el.dataset.cartPreviousTabindex;
                delete el.dataset.cartPreviousPointerEvents;
            }
        });
    }

    updateCartTotal(data) {
        const cartTotal = data.total_price;
        const cartTotalEl = document.querySelectorAll('[cart-total]');
        [...cartTotalEl].forEach((el) => {
            el.textContent = Shopify.formatMoney(
                cartTotal,
                Shopify.money_format
            );
        });
    }

    cartCheckoutEl() {
        const hideContainerClass = 'hide';
        const show = () => {
            if (this.checkoutContainer.classList.contains(hideContainerClass)) {
                this.checkoutContainer.classList.remove(hideContainerClass);
            }
        };
        const hide = () => {
            this.checkoutContainer.classList.add(hideContainerClass);
        };

        return {
            show,
            hide,
        };
    }

    renderElementsBasedOnCartItems(data) {
        this._updateCartItemCount(data);
        if (this.cartItemCount > 0) {
            HELPER_UTIL.dispatchCustomEvent(
                _EVENT_HELPER.showCartRecommendations,
                this
            );
        } else {
            HELPER_UTIL.dispatchCustomEvent(
                _EVENT_HELPER.hideCartRecommendations,
                this
            );
        }
    }

    handleLineItem({ detail }) {
        if (!detail?.key || this.cartUpdateInProgress || this.discountRefreshPending) return;

        const parsedQuantity = Number.parseInt(detail.quantity, 10);
        const quantity = Number.isNaN(parsedQuantity)
            ? 0
            : Math.max(0, parsedQuantity);

        /* Last change for the same line wins; different lines are batched. */
        this.pendingLineItemUpdates.set(detail.key, {
            ...detail,
            key: detail.key,
            quantity,
        });

        /*
         * Removals can be briefly collected into one request. Quantity changes
         * lock all cart controls immediately so Shopify's inventory validation
         * remains on the cart/change endpoint.
         */
        if (quantity === 0) {
            this.quantityElementAction(detail).disable();
        } else {
            this.setCartControlsDisabled(true);
        }

        clearTimeout(this.cartUpdateTimer);
        this.cartUpdateTimer = setTimeout(
            () => this.flushLineItemUpdates(),
            this.cartUpdateDelay
        );
    }

    async flushLineItemUpdates() {
        if (this.cartUpdateInProgress || this.discountRefreshPending ||
            this.pendingLineItemUpdates.size === 0) return;

        const queuedUpdates = Array.from(this.pendingLineItemUpdates.values());
        this.pendingLineItemUpdates.clear();
        this.cartUpdateInProgress = true;
        this.setCartControlsDisabled(true);

        try {
            // Cart keys can change after Regios/Shopify reprice discounted lines.
            // Never submit an identifier that is not in the current cart.
            const root = window.Shopify?.routes?.root || '/';
            const cartResponse = await fetch(`${root}cart.js`, {
                credentials: 'same-origin',
                headers: { Accept: 'application/json' },
                cache: 'no-store',
            });
            if (!cartResponse.ok) throw new Error('Unable to confirm cart items.');
            const liveCart = await cartResponse.json();
            const liveKeys = new Set(liveCart.items.map((item) => item.key));
            if (queuedUpdates.some((update) => !liveKeys.has(update.key))) {
                // Stale UI: refresh instead of changing an unrelated product.
                await this.fetchAndRenderCartItems();
                return;
            }

            const sectionsToRender = HELPER_UTIL.sectionRenderUtil(
                this.cartIdentifier
            ).getSectionsString();
            const sectionsUrl = `${window.location.pathname}${window.location.search}`;
            const isRemovalBatch = queuedUpdates.length > 1 &&
                queuedUpdates.every((update) => update.quantity === 0);
            const update = queuedUpdates[queuedUpdates.length - 1];
            const endpoint = isRemovalBatch ? `${root}cart/update.js` : `${root}cart/change.js`;
            const data = isRemovalBatch
                ? {
                    updates: Object.fromEntries(queuedUpdates.map((item) => [item.key, 0])),
                    sections: sectionsToRender,
                    sections_url: sectionsUrl,
                }
                : {
                    id: update.key,
                    quantity: update.quantity,
                    sections: sectionsToRender,
                    sections_url: sectionsUrl,
                };
            const response = await fetch(endpoint, {
                method: 'POST',
                credentials: 'same-origin',
                headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
                body: JSON.stringify(data),
            });
            if (!response.ok) {
                const error = await response.json().catch(() => ({}));
                if (/no valid id or line parameter/i.test(error.description || error.message || '')) {
                    await this.fetchAndRenderCartItems();
                    return;
                }
                this.handleCartUpdateError(error, queuedUpdates);
                return;
            }
            const result = await response.json();
            if (result.sections?.[this.cartIdentifier]) {
                HELPER_UTIL.dispatchCustomEvent(
                    _EVENT_HELPER.triggerCartChange, this, { sections: result.sections }
                );
            } else {
                await this.fetchAndRenderCartItems();
            }
        } catch (error) {
            this.handleCartUpdateError(error, queuedUpdates);
        } finally {
            this.cartUpdateInProgress = false;
            if (!this.discountRefreshPending) this.setCartControlsDisabled(false);
        }
    }

    handleCartUpdateError(error, queuedUpdates) {
        const fallbackError = {
            status: error?.status || 500,
            message: 'Something went wrong. Please try again.',
        };

        const processError = (responseObj = fallbackError) => {
            queuedUpdates.forEach((update) => {
                HELPER_UTIL.dispatchCustomEvent(
                    _EVENT_HELPER.triggerlineItemError,
                    this,
                    {
                        errorObj: {
                            lineItem: update.key,
                            responseObj: {
                                ...fallbackError,
                                ...responseObj,
                                message:
                                    responseObj?.message || fallbackError.message,
                            },
                            messageType: 'error',
                        },
                    }
                );
            });

            /* Re-render the server-authoritative cart after any failed mutation. */
            this.fetchAndRenderCartItems();
        };

        if (typeof error?.json === 'function') {
            error
                .json()
                .then(processError)
                .catch(() => processError(fallbackError));
        } else {
            processError(fallbackError);
        }
    }

    renderCartContents({ sections }) {
        try {
            const getSectionsToLoop = HELPER_UTIL.sectionRenderUtil(
                this.cartIdentifier
            ).getSectionsObject();
            getSectionsToLoop.forEach((sectionsObjItem) => {
                // Here sections{@object} is the data from the AJAX call we made to cart
                // sectionsObjItem @{object} is the class property consisting list of sections and their ids

                if (!!sections[sectionsObjItem.sectionId]) {
                    [
                        ...document.querySelectorAll(
                            sectionsObjItem.queryElement
                        ),
                    ].forEach((section) => {
                        const renderedSection = HELPER_UTIL.getHTMLFromString(
                            sections[sectionsObjItem.sectionId],
                            sectionsObjItem.queryElement
                        );

                        if (renderedSection) {
                            section.innerHTML = renderedSection.innerHTML;
                        }
                    });
                }
            });
        } catch (error) {
            console.log(error);
        }
    }

    async fetchAndRenderCartItems() {
        const sectionsToRender = HELPER_UTIL.sectionRenderUtil(
            this.cartIdentifier
        ).getSectionsString();
        const requestId = ++this.cartRefreshRequestId;
        const requestUrl = new URL(window.location.href);
        requestUrl.searchParams.set('sections', sectionsToRender);
        requestUrl.hash = '';

        try {
            const response = await fetch(requestUrl.toString(), {
                credentials: 'same-origin',
                headers: { Accept: 'application/json' },
                cache: 'no-store',
            });
            if (!response.ok) throw new Error('Unable to refresh cart.');
            const sections = await response.json();
            if (requestId !== this.cartRefreshRequestId) return;
            if (!sections?.[this.cartIdentifier]) throw new Error('Cart section missing.');
            HELPER_UTIL.dispatchCustomEvent(
                _EVENT_HELPER.triggerCartChange, this, { sections }
            );
        } catch (error) {
            console.error('Cart refresh failed:', error);
        } finally {
            if (requestId === this.cartRefreshRequestId && this.discountRefreshPending) {
                this.discountRefreshPending = false;
                if (!this.cartUpdateInProgress) this.setCartControlsDisabled(false);
            }
        }
    }

    disconnectedCallback() {
        clearTimeout(this.cartUpdateTimer);
        this.pendingLineItemUpdates.clear();
        document.removeEventListener('aminoz:discount-start', this.boundDiscountStart);
        document.removeEventListener('aminoz:discount-failed', this.boundDiscountFailed);

        this.removeEventListener(
            _EVENT_HELPER.OnLineItemUpdate,
            this.boundHandleLineItem
        );
        document.removeEventListener(
            _EVENT_HELPER.triggerCartChange,
            this.boundRenderCart
        );
        document.removeEventListener(
            _EVENT_HELPER.updateAndShowCart,
            this.boundFetchAndRenderCartItems
        );
        document.removeEventListener(
            _EVENT_HELPER.showCartPopup,
            this.boundShowCartPopup
        );
    }
}

customElements.get('drawer-cart') ||
    customElements.define('drawer-cart', DrawerCart);

class CartPage extends DrawerCart {
    constructor() {
        super();
    }

    renderElementsBasedOnCartItems(data) {
        const cartItemCount = this._getCartItemCount(data);
        if (cartItemCount > 0) {
            // this.updateCartTotal(data);
            if (this.checkoutContainer) {
                this.cartCheckoutEl().show();
            }
        } else {
            if (this.checkoutContainer) {
                this.cartCheckoutEl().hide();
            }
        }
    }

    showDrawerToastMessage({ responseObj, messageType }) {
        const toastEl = this.querySelector('toast-message');
        toastEl.toast({
            message: responseObj.message,
            messageType: messageType,
        });
    }
}

customElements.get('cart-page') || customElements.define('cart-page', CartPage);

class DrawerItem extends HTMLElement {
    constructor() {
        super();
        this.itemKey = this.getAttribute('item-key'); // Unique line item key of the product in cart
        this.removeItemElement = this.querySelectorAll('[remove-lineItem]');

        this.boundDispatchLineItemUpdate =
            this.dispatchLineItemUpdate.bind(this);
        this.boundHandleRemoveKeyup = this.handleRemoveKeyup.bind(this);
        this.boundShowLineItemError = this.showLineItemError.bind(this);

        [...this.removeItemElement].forEach((el) => {
            el.addEventListener('click', this.boundDispatchLineItemUpdate);
            el.addEventListener('keyup', this.boundHandleRemoveKeyup);
        });

        this.addEventListener(
            _EVENT_HELPER.onQuantityChange,
            this.boundDispatchLineItemUpdate
        );

        document.addEventListener(
            _EVENT_HELPER.triggerlineItemError,
            this.boundShowLineItemError
        );
    }

    handleRemoveKeyup(e) {
        if (e.key === 'Enter') {
            this.dispatchLineItemUpdate(e);
        }
    }

    dispatchLineItemUpdate(e) {
        const actionElement = e?.target?.closest?.('[remove-lineItem]');
        if (actionElement?.getAttribute('aria-disabled') === 'true') return;

        const hasQuantity =
            e?.detail &&
            Object.prototype.hasOwnProperty.call(e.detail, 'quantity');
        const productQuantity = hasQuantity ? e.detail.quantity : '0';
        const detail = {
            key: this.itemKey,
            quantity: String(productQuantity),
        };

        if (detail.quantity === '0') {
            const animatedItem = e?.target?.closest?.('[animation-slide-left]');
            if (animatedItem && animatedItem.dataset.cartRemoving !== 'true') {
                animatedItem.dataset.cartRemoving = 'true';
                Motion.animate(
                    animatedItem,
                    {
                        x: 40,
                        opacity: 0,
                    },
                    {
                        duration: parseFloat(0.3),
                        ease: 'cubic-bezier(.75,0,.175,1)',
                    }
                );
            }
        }

        HELPER_UTIL.dispatchCustomEvent(
            _EVENT_HELPER.OnLineItemUpdate,
            this,
            detail
        );
    }

    handleLineItemErrorUpdate(errorObj) {
        const customMessageComponent =
            this.querySelector('custom-message') ||
            this.nextElementSibling?.querySelector('custom-message');

        customMessageComponent?.showCustomMessage({
            errorMessage: errorObj.responseObj.message,
            messageType: errorObj.messageType,
        });
    }

    resetQuantitySelector() {
        const quantityInput = this.querySelector('quantity-selector input');
        if (quantityInput) {
            quantityInput.value = this.getAttribute('item-quantity');
        }
    }

    resetRemovalAnimation() {
        const animatedItem = this.closest('[animation-slide-left]');
        if (!animatedItem) return;

        delete animatedItem.dataset.cartRemoving;
        Motion.animate(
            animatedItem,
            {
                x: 0,
                opacity: 1,
            },
            {
                duration: parseFloat(0.2),
                ease: 'cubic-bezier(.75,0,.175,1)',
            }
        );
    }

    showLineItemError(event) {
        const { errorObj } = event.detail;
        if (errorObj && errorObj.lineItem === this.itemKey) {
            this.handleLineItemErrorUpdate(errorObj);
            this.resetQuantitySelector();
            this.resetRemovalAnimation();
        }
    }

    disconnectedCallback() {
        this.removeEventListener(
            _EVENT_HELPER.onQuantityChange,
            this.boundDispatchLineItemUpdate
        );

        [...this.removeItemElement].forEach((el) => {
            el.removeEventListener('click', this.boundDispatchLineItemUpdate);
            el.removeEventListener('keyup', this.boundHandleRemoveKeyup);
        });

        document.removeEventListener(
            _EVENT_HELPER.triggerlineItemError,
            this.boundShowLineItemError
        );
    }
}

customElements.get('drawer-item') ||
    customElements.define('drawer-item', DrawerItem);



//More checkout button animation 

class AdditionalCheckoutButtonsAnimation extends HTMLElement {
    constructor() {
        super();
        this.toggleButton = this.querySelector("[toggle-payment-buttons]")
        this.toggleButton?.addEventListener("click", this.togglePaymentButtons.bind(this))
        this.buttonsWrapper = this.querySelector("[checkout-buttons-wrapper]")

    }

    togglePaymentButtons(e) {
        e.preventDefault()
        this.toggleButton.classList.toggle("show-payment-options")
        this.setAttribute("style", `--button-wrapper-height:${this.buttonsWrapper?.firstElementChild.offsetHeight}px`)
        this.buttonsWrapper?.classList.toggle("active")
    }

}

customElements.get('additional-checkout-buttons-animation') ||
    customElements.define("additional-checkout-buttons-animation", AdditionalCheckoutButtonsAnimation)